import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { createPage, listPages } from "../api/reports";
import { ApiError } from "../api/client";
import type { TenantPage } from "../types";
import { Modal } from "../components/primitives";
import { PageHeader } from "../components/Chrome";
import { toast } from "../components/Toast";

export function AdminPages({ tenantSlug }: { tenantSlug: string }) {
  const [pages, setPages] = useState<TenantPage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const reload = async () => {
    try {
      setPages(await listPages(tenantSlug));
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : (e as Error).message);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug]);

  return (
    <div className="container">
      <PageHeader title="Pages">
        <button className="btn-primary" onClick={() => setCreateOpen(true)}>
          + New page
        </button>
      </PageHeader>
      {error && <div className="empty err">{error}</div>}
      {pages === null && !error && <div className="empty">Loading…</div>}
      {pages && pages.length === 0 && (
        <div className="empty">
          No pages yet. Click "+ New page" to add one.
        </div>
      )}
      {pages && pages.length > 0 && (
        <div className="page-cards">
          {pages.map((p) => (
            <Link key={p.id} to={`/admin/pages/${p.id}`} className="page-card">
              <div className="page-card-name">{p.name}</div>
              {p.description && (
                <div className="page-card-desc">{p.description}</div>
              )}
              <div className="page-card-meta">
                slug: <code>{p.slug}</code>
              </div>
            </Link>
          ))}
        </div>
      )}
      {createOpen && (
        <CreatePageModal
          tenantSlug={tenantSlug}
          onClose={() => setCreateOpen(false)}
          onCreated={async () => {
            setCreateOpen(false);
            await reload();
          }}
        />
      )}
    </div>
  );
}

function CreatePageModal({
  tenantSlug,
  onClose,
  onCreated,
}: {
  tenantSlug: string;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [dashboardId, setDashboardId] = useState("");
  const [collectionId, setCollectionId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      await createPage(tenantSlug, {
        slug,
        name,
        description: description || null,
        metabase_dashboard_id: dashboardId ? Number(dashboardId) : null,
        metabase_collection_id: collectionId ? Number(collectionId) : null,
      });
      toast(`Created page ${name}`);
      await onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail : (e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} width={480}>
      <h2>New page</h2>
      <p style={{ color: "var(--ink-3)" }}>
        Each page wraps one Metabase dashboard. Slug is the URL-safe identifier.
      </p>
      <label className="field-label">Slug</label>
      <input
        type="text"
        placeholder="e.g. client-performance"
        value={slug}
        onChange={(e) => setSlug(e.target.value)}
        className="text-input"
        autoFocus
      />
      <label className="field-label">Display name</label>
      <input
        type="text"
        placeholder="e.g. Client Performance"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="text-input"
      />
      <label className="field-label">Description (optional)</label>
      <input
        type="text"
        placeholder="Brief description visible on the hub"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="text-input"
      />
      <label className="field-label">Metabase dashboard ID (optional)</label>
      <input
        type="number"
        placeholder="e.g. 42"
        value={dashboardId}
        onChange={(e) => setDashboardId(e.target.value)}
        className="text-input"
      />
      <label className="field-label">Metabase collection ID (optional)</label>
      <input
        type="number"
        placeholder="e.g. 100"
        value={collectionId}
        onChange={(e) => setCollectionId(e.target.value)}
        className="text-input"
      />
      {err && <div className="form-err">{err}</div>}
      <div className="modal-actions">
        <button className="btn-secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button
          className="btn-primary"
          onClick={submit}
          disabled={!slug || !name || submitting}
        >
          {submitting ? "Creating…" : "Create page"}
        </button>
      </div>
    </Modal>
  );
}
